import type { WavespeedModelConfig } from '../../types';
import { resolveImageSize, clampTo } from '../../../tools';

export const qwenConfigs: Record<string, WavespeedModelConfig> = {
    'alibaba/qwen-image-3.0/text-to-image': {
        // https://wavespeed.ai/zh-CN/models/alibaba/qwen-image-3.0/text-to-image
        apiPath: '/alibaba/qwen-image-3.0/text-to-image',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, aspectRatio, image_resolution, providerOptions } = params;
            return {
                prompt: prompt.slice(0, 800),
                aspect_ratio: clampTo(aspectRatio || '1:1', ['1:1', '1:2', '2:1', '1:3', '3:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '9:21', '21:9'] as const, '1:1'),
                resolution: clampTo(image_resolution?.toLowerCase() || '1k', ['1k', '2k'] as const, '1k'),
                seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                // 可选参数
                enable_prompt_expansion: typeof providerOptions?.enable_prompt_expansion === 'boolean'
                    ? providerOptions.enable_prompt_expansion
                    : true,
            };
        },
    },
    'alibaba/qwen-image-3.0/edit': {
        // https://wavespeed.ai/zh-CN/models/alibaba/qwen-image-3.0/edit
        apiPath: '/alibaba/qwen-image-3.0/edit',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, image_resolution, providerOptions } = params;
            return {
                prompt: prompt.slice(0, 800),
                aspect_ratio: aspectRatio === 'match'
                    ? undefined
                    : clampTo(aspectRatio || '1:1', ['1:1', '1:2', '2:1', '1:3', '3:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '9:21', '21:9'] as const, '1:1'),
                resolution: clampTo(image_resolution?.toLowerCase() || '1k', ['1k', '2k'] as const, '1k'),
                seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                // 图片 URL
                images: image_urls.slice(0, 3),
                // 可选参数
                enable_prompt_expansion: typeof providerOptions?.enable_prompt_expansion === 'boolean'
                    ? providerOptions.enable_prompt_expansion
                    : true,
            };
        },
    },
    'wavespeed-ai/qwen-image-2.1/text-to-image': {
        // https://wavespeed.ai/models/wavespeed-ai/qwen-image-2.1/text-to-image
        apiPath: '/wavespeed-ai/qwen-image-2.1/text-to-image',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, aspectRatio, image_resolution, providerOptions } = params;
            return {
                prompt: prompt,
                aspect_ratio: clampTo(aspectRatio || '1:1', ['1:1', '1:2', '2:1', '1:3', '3:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '9:21', '21:9'] as const, '1:1'),
                resolution: clampTo(image_resolution?.toLowerCase() || '1k', ['1k', '1.5k', '2k'] as const, '1k'),
                seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                // 可选参数
                output_format: clampTo(
                    providerOptions?.output_format || 'png',
                    ['png', 'jpeg', 'webp'] as const,
                    'png',
                ),
            };
        },
    },
    'wavespeed-ai/qwen-image-2.1/edit': {
        // https://wavespeed.ai/models/wavespeed-ai/qwen-image-2.1/edit
        apiPath: '/wavespeed-ai/qwen-image-2.1/edit',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, image_resolution, providerOptions } = params;
            return {
                prompt: prompt,
                aspect_ratio: aspectRatio === 'match'
                    ? undefined
                    : clampTo(aspectRatio || '1:1', ['1:1', '1:2', '2:1', '1:3', '3:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '9:21', '21:9'] as const, '1:1'),
                resolution: clampTo(image_resolution?.toLowerCase() || '1k', ['1k', '1.5k', '2k'] as const, '1k'),
                seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                // 图片 URL
                images: image_urls.slice(0, 10),
                // 可选参数
                output_format: clampTo(
                    providerOptions?.output_format || 'png',
                    ['png', 'jpeg', 'webp'] as const,
                    'png',
                ),
            };
        },
    },
    'alibaba/qwen-image-3.0-pro/text-to-image': {
        // https://wavespeed.ai/zh-CN/models/alibaba/qwen-image-3.0-pro/text-to-image
        apiPath: '/alibaba/qwen-image-3.0-pro/text-to-image',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, aspectRatio, image_resolution, providerOptions } = params;
            return {
                prompt: prompt.slice(0, 800),
                aspect_ratio: clampTo(aspectRatio || '1:1', ['1:1', '1:2', '2:1', '1:3', '3:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '9:21', '21:9'] as const, '1:1'),
                resolution: clampTo(image_resolution?.toLowerCase() || '1k', ['1k', '2k'] as const, '1k'),
                seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                // 可选参数
                enable_prompt_expansion: typeof providerOptions?.enable_prompt_expansion === 'boolean'
                    ? providerOptions.enable_prompt_expansion
                    : true,
            };
        },
    },
    'alibaba/qwen-image-3.0-pro/edit': {
        // https://wavespeed.ai/zh-CN/models/alibaba/qwen-image-3.0-pro/edit
        apiPath: '/alibaba/qwen-image-3.0-pro/edit',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, image_resolution, providerOptions } = params;
            return {
                prompt: prompt.slice(0, 800),
                aspect_ratio: aspectRatio === 'match'
                    ? undefined
                    : clampTo(aspectRatio || '1:1', ['1:1', '1:2', '2:1', '1:3', '3:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '9:21', '21:9'] as const, '1:1'),
                resolution: clampTo(image_resolution?.toLowerCase() || '1k', ['1k', '2k'] as const, '1k'),
                seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                // 图片 URL
                images: image_urls.slice(0, 3),
                // 可选参数
                enable_prompt_expansion: typeof providerOptions?.enable_prompt_expansion === 'boolean'
                    ? providerOptions.enable_prompt_expansion
                    : true,
            };
        },
    },
    'qwen-image-2.0-pro/text-to-image': {
        // https://wavespeed.ai/models/wavespeed-ai/qwen-image-2.0-pro/text-to-image
        apiPath: '/wavespeed-ai/qwen-image-2.0-pro/text-to-image',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, aspectRatio, providerOptions} = params;
            return {
                prompt: prompt,
                size: resolveImageSize(
                    clampTo(aspectRatio || '16:9', ['21:9', '9:21', '16:9', '9:16', '9:7', '7:9', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '16:9'), 'z-image-turbo'),
                seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
            };
        },
    },
    'qwen-image-2.0-pro/edit': {
        // https://wavespeed.ai/models/wavespeed-ai/qwen-image-2.0-pro/edit
        apiPath: '/wavespeed-ai/qwen-image-2.0-pro/edit',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, size, aspectRatio, seed, image_size, image_resolution, providerOptions, n } = params;
            return {
                prompt: prompt,
                size: aspectRatio === 'match' ? undefined : resolveImageSize(
                    clampTo(aspectRatio || '16:9', ['21:9', '9:21', '16:9', '9:16', '9:7', '7:9', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '16:9'), 'z-image-turbo'),
                seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                // 图片 URL
                images: image_urls.slice(0, 6),
            };
        },
    },
    'qwen-image-2.0/text-to-image': {
        // https://wavespeed.ai/models/wavespeed-ai/qwen-image-2.0/text-to-image
        apiPath: '/wavespeed-ai/qwen-image-2.0/text-to-image',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt,  aspectRatio, providerOptions } = params;
            return {
                prompt: prompt,
                size: resolveImageSize(
                    clampTo(aspectRatio || '16:9', ['21:9', '9:21', '16:9', '9:16', '9:7', '7:9', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '16:9'), 'z-image-turbo'
                ),
                seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
            };
        },
    },
    'qwen-image-2.0/edit': {
        // https://wavespeed.ai/models/wavespeed-ai/qwen-image-2.0/edit
        apiPath: '/wavespeed-ai/qwen-image-2.0/edit',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, providerOptions} = params;
            return {
                prompt: prompt,
                size: aspectRatio === 'match' ? undefined : resolveImageSize(
                    clampTo(aspectRatio || '16:9', ['21:9', '9:21', '16:9', '9:16', '9:7', '7:9', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '16:9'), 'z-image-turbo'
                ),
                seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                // 图片 URL
                images: image_urls.slice(0, 6),
            };
        },
    },
    'qwen-image-max/text-to-image': {
        // https://wavespeed.ai/models/wavespeed-ai/qwen-image-max/text-to-image
        apiPath: '/wavespeed-ai/qwen-image-max/text-to-image',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, aspectRatio, providerOptions } = params;
            return {
                prompt: prompt,
                size: resolveImageSize(
                    clampTo(aspectRatio || '16:9', ['21:9', '9:21', '16:9', '9:16', '9:7', '7:9', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '16:9'), 'z-image-turbo'
                ),
                seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
            };
        },
    },
    'qwen-image-max/edit': {
        // https://wavespeed.ai/models/wavespeed-ai/qwen-image-max/edit
        apiPath: '/wavespeed-ai/qwen-image-max/edit',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, seed, providerOptions } = params;
            return {
                prompt: prompt,
                size: aspectRatio === 'match' ? undefined : resolveImageSize(
                    clampTo(aspectRatio || '16:9', ['21:9', '9:21', '16:9', '9:16', '9:7', '7:9', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '16:9'), 'z-image-turbo'
                ),
                seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                // 图片 URL
                images: image_urls.slice(0, 6),
            };
        },
    },
    'qwen-image/edit-plus': {
        // https://wavespeed.ai/zh-CN/models/wavespeed-ai/qwen-image/edit-plus
        apiPath: '/wavespeed-ai/qwen-image/edit-plus',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, providerOptions } = params;
            return {
                prompt: prompt,
                size: aspectRatio === 'match' ? undefined : resolveImageSize(
                    clampTo(aspectRatio || '16:9', ['21:9', '9:21', '16:9', '9:16', '9:7', '7:9', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '16:9'), 'z-image-turbo'
                ),
                seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                // 图片 URL
                images: image_urls.slice(0, 3),
                // 可选参数
                output_format: clampTo(
                    providerOptions?.output_format || 'png',
                    ['png', 'jpeg', 'webp'] as const,
                    'png',
                ),
            };
        },
    },
    'qwen-image/text-to-image': {
        // https://wavespeed.ai/zh-CN/models/wavespeed-ai/qwen-image/text-to-image
        apiPath: '/wavespeed-ai/qwen-image/text-to-image',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, aspectRatio, providerOptions } = params;
            return {
                prompt: prompt,
                size: resolveImageSize(
                    clampTo(aspectRatio || '16:9', ['21:9', '9:21', '16:9', '9:16', '9:7', '7:9', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '16:9'), 'z-image-turbo'
                ),
                seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                // 可选参数
                output_format: clampTo(
                    providerOptions?.output_format || 'png',
                    ['png', 'jpeg', 'webp'] as const,
                    'png',
                ),
            };
        },
    },
    'qwen-image/edit': {
        // https://wavespeed.ai/zh-CN/models/wavespeed-ai/qwen-image/edit
        apiPath: '/wavespeed-ai/qwen-image/edit',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, providerOptions } = params;
            return {
                prompt: prompt,
                size: aspectRatio === 'match' ? undefined : resolveImageSize(
                    clampTo(aspectRatio || '16:9', ['21:9', '9:21', '16:9', '9:16', '9:7', '7:9', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '16:9'), 'z-image-turbo'
                ),
                seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                // 图片 URL
                images: image_urls[0],
                // 可选参数
                output_format: clampTo(
                    providerOptions?.output_format || 'png',
                    ['png', 'jpeg', 'webp'] as const,
                    'png',
                ),
            };
        },
    },
};